import {
  availableSlotsToStartTimes,
  rangesOverlap,
} from "@/lib/appointments/calendar-utils";
import type { Appointment } from "@/types/appointments/api-types/appointment-types";
import type {
  StaffAvailability,
  TimeRange,
} from "@/types/appointments/api-types/availability-types";
import type { Staff } from "@/types/staff/api-types/staff-types";
import type {
  StaffServiceSummaryItem,
  StaffServicesSummaryResponse,
} from "@/types/staff/api-types/staff-services-summary-types";
import type { RowConflict } from "@/types/appointments/appointment-form-types";
import type { AddAppointmentFormValues } from "@/zod-schemas/appointments/add-appointment-schema";

type ServiceLine = AddAppointmentFormValues["services"][number];

export const staffLabel = (staff: Staff): string =>
  staff.preferred_name || `${staff.first_name} ${staff.last_name}`;

/** Display-only sum of form line `price`s — not a business rule. */
export const sumLinePrices = (lines: { price: number }[]): number =>
  lines.reduce((total, line) => total + line.price, 0);

/** Keep the booked HH:MM in the Time list so the Select still shows it while we warn. */
export const withExistingStartTime = (
  times: string[],
  startTime: string
): string[] => {
  if (startTime && !times.includes(startTime)) {
    return [...times, startTime].sort();
  }
  return times;
};

/** Keep a non-15-minute booked duration in the Duration list so the Select still shows it. */
export const withExistingDuration = (durations: readonly number[], durationMinutes: number): number[] =>
  durationMinutes <= 0 || durations.includes(durationMinutes) ? [...durations] : [...durations, durationMinutes];

/** Keep the booked service in the Service list when the new stylist does not offer it. */
export const withExistingService = (
  options: StaffServiceSummaryItem[],
  serviceId: number,
  fallback: StaffServiceSummaryItem
): StaffServiceSummaryItem[] => {
  if (serviceId === 0 || options.some((item) => item.service_id === serviceId)) {
    return options;
  }
  return [...options, fallback];
};

/** Free HH:MM starts that fit this line's duration — used for conflict, not the Select list. */
export const availableTimesForLine = (
  line: ServiceLine,
  slotRanges: TimeRange[]
): string[] =>
  availableSlotsToStartTimes(
    slotRanges,
    line.duration_minutes > 0 ? line.duration_minutes : undefined
  );

export const getRowConflict = ({
  line,
  availabilityReady,
  rosterIds,
  serviceOptions,
  slotRanges,
  servicesSummaryReady,
}: {
  line: ServiceLine;
  availabilityReady: boolean;
  rosterIds: Set<number>;
  serviceOptions: StaffServiceSummaryItem[];
  slotRanges: TimeRange[];
  servicesSummaryReady: boolean;
}): RowConflict => {
  // Do not warn while the new day's availability is still loading (empty roster looks like "not working").
  if (line.staff_id === 0 || !availabilityReady) return null;
  if (!rosterIds.has(line.staff_id)) return "stylist";
  if (line.service_id === 0) return null;
  if (
    servicesSummaryReady &&
    !serviceOptions.some((item) => item.service_id === line.service_id)
  ) {
    return "service";
  }
  if (!line.start_time) return null;
  const durationFitTimes = availableTimesForLine(line, slotRanges);
  if (durationFitTimes.includes(line.start_time)) return null;
  // Clock time is a free start, but this duration does not fit the gap.
  const slotStartTimes = availableSlotsToStartTimes(slotRanges);
  if (slotStartTimes.includes(line.start_time)) return "duration";
  return "time";
};

/** Bookable services for one row's stylist (needs duration + price to PUT). */
export const getServiceOptions = (
  staffId: number,
  servicesSummary: StaffServicesSummaryResponse | undefined
): StaffServiceSummaryItem[] => {
  if (staffId === 0) return [];
  const selectedSummary = servicesSummary?.staff.find(
    (entry) => entry.staff_id === staffId
  );
  return (selectedSummary?.services ?? []).filter(
    (service) => service.duration_minutes != null && service.price != null
  );
};

export const slotRangesForStaff = (
  staffId: number,
  rosterStaff: StaffAvailability[]
): TimeRange[] => {
  const selectedAvailability = rosterStaff.find(
    (entry) => Number(entry.staff_id) === Number(staffId)
  );
  return (
    selectedAvailability?.available_slots ??
    selectedAvailability?.working_hours ??
    []
  );
};

export const serviceNameFor = (
  serviceId: number,
  servicesSummary: StaffServicesSummaryResponse | undefined,
  appointment?: Appointment
): string => {
  const fromAppointment = appointment?.services.find(
    (item) => item.service_id === serviceId
  );
  if (fromAppointment?.service_name) return fromAppointment.service_name;
  for (const entry of servicesSummary?.staff ?? []) {
    const match = entry.services.find((item) => item.service_id === serviceId);
    if (match) return match.service_name;
  }
  return "this service";
};

export const lineOverlapsSibling = (
  line: ServiceLine,
  index: number,
  lines: ServiceLine[]
): boolean => {
  if (line.staff_id === 0 || !line.start_time || line.duration_minutes <= 0) {
    return false;
  }
  return lines.some((other, otherIndex) => {
    if (otherIndex === index) return false;
    if (other.staff_id !== line.staff_id) return false;
    if (other.staff_id === 0 || !other.start_time || other.duration_minutes <= 0) {
      return false;
    }
    return rangesOverlap(
      line.start_time,
      line.duration_minutes,
      other.start_time,
      other.duration_minutes
    );
  });
};
